/**
 * Subscription tier definitions and feature entitlements.
 *
 * Single source of truth for both UI gating (this module) and the
 * backend tier_gate enforcement (mirrored in program/src/api/tier_gate.rs).
 * Keep both in sync.
 */

export const TIER_KEYS = ['observer', 'managed', 'professional', 'team', 'enterprise'] as const;
export type TierKey = (typeof TIER_KEYS)[number];

export const TIER_RANK: Record<TierKey, number> = {
  observer: 0,
  managed: 1,
  professional: 2,
  team: 3,
  enterprise: 4,
};

export const TIER_LABEL: Record<TierKey, string> = {
  observer: 'Observer',
  managed: 'Managed',
  professional: 'Professional',
  team: 'Team',
  enterprise: 'Enterprise',
};

export interface TierFeatures {
  liveTrading: boolean;
  geneticAlgo: boolean;
  workflows: boolean;
  /** -1 = unlimited */
  maxBrokerConnections: number;
  /** -1 = unlimited */
  maxStrategies: number;
  /** -1 = unlimited */
  maxConcurrentBacktests: number;
  /** -1 = unlimited */
  historyMonths: number;
  /** Monthly genetic optimization jobs. -1 = unlimited, 0 = feature locked. */
  monthlyGeneticJobs: number;
  /** Monthly backtest submissions. -1 = unlimited. Observer-only cost-containment cap. */
  maxMonthlyBacktests: number;
  /** Max assets in a portfolio backtest. 0 = no portfolio mode, -1 = unlimited. */
  portfolioAssets: number;
  /** Rebalancing capabilities: 'none' | 'calendar' | 'all'. */
  portfolioRebalancing: 'none' | 'calendar' | 'all';
  /**
   * True when this tier's broker connection(s) must be paper/testnet-only
   * (never place real-money orders). Mirrors `tier_gate::paper_only` in the
   * backend -- keep both in sync. D-FREE-PAPER (design council, 2026-10-15):
   * paper trading is free, so Observer gets exactly one FREE paper-only
   * broker connection (see `maxBrokerConnections` below) instead of zero.
   * Enforced server-side in `exchange_credentials::add_exchange` against
   * `SupportedExchange::is_paper_only` -- this flag drives UI framing/
   * filtering only, it is not itself the security boundary.
   */
  paperOnly: boolean;
}

export const TIER_FEATURES: Record<TierKey, TierFeatures> = {
  observer: {
    liveTrading: false,
    geneticAlgo: false,
    workflows: false,
    // D-FREE-PAPER (design council, 2026-10-15): paper trading is free --
    // Observer may connect exactly ONE paper-only broker account and run
    // the full mandate -> proposal -> paper-run loop end to end. Live
    // trading is unchanged (still gated by `liveTrading: false`).
    maxBrokerConnections: 1,
    paperOnly: true,
    maxStrategies: 5,
    maxConcurrentBacktests: 2,
    historyMonths: 12,
    monthlyGeneticJobs: 0,
    maxMonthlyBacktests: 10,
    portfolioAssets: 0,
    portfolioRebalancing: 'none',
  },
  managed: {
    liveTrading: true,
    geneticAlgo: false,
    workflows: false,
    maxBrokerConnections: 1,
    paperOnly: false,
    maxStrategies: 25,
    maxConcurrentBacktests: 3,
    historyMonths: 24,
    monthlyGeneticJobs: 0,
    maxMonthlyBacktests: -1,
    portfolioAssets: 3,
    portfolioRebalancing: 'calendar',
  },
  professional: {
    liveTrading: true,
    geneticAlgo: true,
    workflows: true,
    maxBrokerConnections: 3,
    paperOnly: false,
    maxStrategies: 100,
    maxConcurrentBacktests: 10,
    historyMonths: 60,
    monthlyGeneticJobs: 50,
    maxMonthlyBacktests: -1,
    portfolioAssets: 10,
    portfolioRebalancing: 'all',
  },
  team: {
    liveTrading: true,
    geneticAlgo: true,
    workflows: true,
    maxBrokerConnections: 10,
    paperOnly: false,
    maxStrategies: 500,
    maxConcurrentBacktests: 25,
    historyMonths: 120,
    monthlyGeneticJobs: 200,
    maxMonthlyBacktests: -1,
    portfolioAssets: 25,
    portfolioRebalancing: 'all',
  },
  enterprise: {
    liveTrading: true,
    geneticAlgo: true,
    workflows: true,
    maxBrokerConnections: -1,
    paperOnly: false,
    maxStrategies: -1,
    maxConcurrentBacktests: -1,
    historyMonths: -1,
    monthlyGeneticJobs: -1,
    maxMonthlyBacktests: -1,
    portfolioAssets: -1,
    portfolioRebalancing: 'all',
  },
};

export type FeatureKey = {
  [K in keyof TierFeatures]: TierFeatures[K] extends boolean ? K : never;
}[keyof TierFeatures];

export function normalizeTier(tier: string | null | undefined): TierKey {
  if (!tier) return 'observer';
  const lower = tier.toLowerCase();
  if ((TIER_KEYS as readonly string[]).includes(lower)) {
    return lower as TierKey;
  }
  // Backward-compat aliases (legacy DB rows / API callers). "explorer"/"trader"
  // are the pre-2026-10-09 names for "observer"/"managed".
  if (lower === 'free' || lower === 'explorer') return 'observer';
  if (lower === 'trader') return 'managed';
  if (lower === 'pro') return 'professional';
  if (lower === 'live') return 'team';
  if (lower === 'institution') return 'team';
  return 'observer';
}

export function meetsTier(current: string | null | undefined, required: TierKey): boolean {
  return TIER_RANK[normalizeTier(current)] >= TIER_RANK[required];
}

export function hasFeature(tier: string | null | undefined, feature: FeatureKey): boolean {
  return TIER_FEATURES[normalizeTier(tier)][feature];
}

/** First tier that satisfies a boolean feature, for "Upgrade to X" CTAs. */
export function minTierFor(feature: FeatureKey): TierKey {
  for (const t of TIER_KEYS) {
    if (TIER_FEATURES[t][feature]) return t;
  }
  return 'enterprise';
}

export function tierLabel(tier: string | null | undefined): string {
  return TIER_LABEL[normalizeTier(tier)];
}

/** True if a quota cap (-1 = unlimited) has been reached. */
export function isAtCap(current: number, cap: number): boolean {
  if (cap < 0) return false;
  return current >= cap;
}

// --- Pricing ---

export type BillingInterval = 'monthly' | 'annual';

/**
 * Self-reported AUM bands -- mirrors the exact allow-list validated
 * server-side in `program/src/api/settings.rs`'s `AUM_BANDS` and collected
 * during onboarding (`CompanyProfileModal`) / Settings (`ProfileSettings`).
 * Reused here as the second pricing dimension for Professional/Team: a
 * $500k solo operator and a $50M fund shouldn't see the same number.
 */
export const AUM_BANDS = ['under_1m', '1m_10m', '10m_100m', '100m_plus'] as const;
export type AumBand = (typeof AUM_BANDS)[number];

export const AUM_BAND_LABEL: Record<AumBand, string> = {
  under_1m: 'Under $1M',
  '1m_10m': '$1M – $10M',
  '10m_100m': '$10M – $100M',
  '100m_plus': '$100M+',
};

/**
 * Bands sold through self-serve Stripe checkout at Professional/Team.
 * 10m_100m and 100m_plus deliberately have no self-serve price at all --
 * see `requiresEnterpriseSales`. A book that size is exactly the buyer who
 * needs a real security/compliance conversation before signing up, not a
 * bigger number on a checkout button.
 */
export const SELF_SERVE_AUM_BANDS: readonly AumBand[] = ['under_1m', '1m_10m'];

type BandPricedTier = 'professional' | 'team';
const BAND_PRICED_TIERS: readonly BandPricedTier[] = ['professional', 'team'];
function isBandPricedTier(tier: TierKey): tier is BandPricedTier {
  return (BAND_PRICED_TIERS as readonly TierKey[]).includes(tier);
}

/**
 * True when this tier+band combination isn't sold self-serve and should
 * route to "Contact Sales" instead of Stripe checkout. A missing band is
 * NOT treated as requiring sales -- that's a "please tell us your AUM"
 * state, not a routing decision.
 *
 * NOTE (2026-10-09 capital-fee-billing repricing): this AUM-band mechanism
 * now governs ONLY self-serve-checkout-vs-sales routing, never the flat
 * subscription price -- see `FLAT_MONTHLY_PRICE`'s doc comment for why the
 * price itself stopped varying by band. Kept as-is (not rebased onto
 * capital-under-mandate) because CUM is only known AFTER a mandate is
 * accepted and live, while this check runs at checkout time, before any
 * capital is under mandate at all -- AUM band (self-reported) is the right
 * signal for THIS gate, not a stand-in for one. Mirrors
 * `tier_gate::requires_enterprise_sales` in the backend.
 */
export function requiresEnterpriseSales(tier: TierKey, band: AumBand | null | undefined): boolean {
  if (!isBandPricedTier(tier)) return false;
  if (!band) return false;
  return !SELF_SERVE_AUM_BANDS.includes(band);
}

/**
 * Flat monthly subscription price in USD, per the 2026-10-09 repricing.
 * Replaces the old AUM-band-varying `BAND_MONTHLY_PRICE`/`BAND_ANNUAL_PRICE`
 * for Professional/Team -- pricing is now a flat per-tier subscription fee
 * plus a separate capital-under-mandate basis-point fee
 * (`CAPITAL_FEE_ANNUAL_BPS`), which carries the AUM-sensitivity the old
 * banded flat price used to carry. The self-serve-vs-sales AUM-band gate
 * (`requiresEnterpriseSales`) is unchanged and still applies independently
 * of this price. Mirrors `tier_gate::flat_monthly_price_usd` in the backend
 * -- keep both in sync (same rule as `TIER_FEATURES` above). No entry for
 * `enterprise` -- its price is custom/negotiated (see `tierPrice`).
 */
export const FLAT_MONTHLY_PRICE: Record<'observer' | 'managed' | 'professional' | 'team', number> = {
  observer: 0,
  managed: 99,
  professional: 299,
  team: 599,
};
/** Annual equivalent of `FLAT_MONTHLY_PRICE`. Mirrors `tier_gate::flat_annual_price_usd`. */
export const FLAT_ANNUAL_PRICE: Record<'observer' | 'managed' | 'professional' | 'team', number> = {
  observer: 0,
  managed: 990,
  professional: 2990,
  team: 5990,
};

/**
 * Annual basis-point rate charged on a tenant's live capital-under-mandate
 * (CUM) -- real capital actually running under an accepted mandate, NEVER
 * paper-mode usage. 1 bp = 0.01%; e.g. `managed: 20` is 0.20%/yr. No entry
 * for `observer` (can't hold a live mandate at all) or `enterprise`
 * (negotiated per contract). Mirrors `tier_gate::capital_fee_annual_bps` in
 * the backend -- keep both in sync. Display-only here (the GUI doesn't
 * compute real fees; `capital_fee_scheduler.rs` on the backend does).
 */
export const CAPITAL_FEE_ANNUAL_BPS: Record<'managed' | 'professional' | 'team', number> = {
  managed: 20,
  professional: 15,
  team: 10,
};

/**
 * Display price string for a tier and interval. 2026-10-09: every paid tier
 * is flat now (no AUM-band-dependent range/exact-price branching) -- the
 * `aumBand` parameter stays for callers that still need to decide
 * self-serve-vs-sales routing (via `requiresEnterpriseSales`) before showing
 * a price at all; once that's decided, the number itself never varies by
 * band.
 */
export function tierPrice(tier: TierKey, interval: BillingInterval, aumBand?: AumBand | null): string {
  if (tier === 'enterprise') return 'Custom';
  if (isBandPricedTier(tier) && requiresEnterpriseSales(tier, aumBand)) return 'Custom';

  const amount = interval === 'annual'
    ? FLAT_ANNUAL_PRICE[tier as 'observer' | 'managed' | 'professional' | 'team']
    : FLAT_MONTHLY_PRICE[tier as 'observer' | 'managed' | 'professional' | 'team'];
  return `$${amount.toLocaleString()}`;
}

/** Display period suffix for a tier and interval. */
export function tierPeriod(tier: TierKey, interval: BillingInterval): string {
  if (tier === 'enterprise') return '';
  if (tier === 'observer') return ' forever';
  return interval === 'annual' ? '/yr' : '/mo';
}

// --- AI credit budgets ---
// Mirrors `monthly_ai_budget_usd` / `monthly_workflow_budget_usd` in
// program/src/api/tier_gate.rs. Keep both in sync (same rule as
// TIER_FEATURES above). Everyday AI budget covers single-call actions
// (interview, Explain, Diagnose, Generate, ...); workflow budget is a
// SEPARATE pool for the autonomous multi-iteration search agent, because
// its per-run cost variance is far wider than a single interview turn.
// `null` for everyday = unlimited (Enterprise); `null` for workflow also
// covers "not applicable" for tiers without the `workflows` feature.
export const TIER_AI_BUDGET_USD: Record<TierKey, number | null> = {
  observer: 0.75,
  managed: 3.00,
  professional: 10.00,
  team: 20.00,
  enterprise: null,
};

export const TIER_WORKFLOW_BUDGET_USD: Record<TierKey, number | null> = {
  observer: null,
  managed: null,
  professional: 35.00,
  team: 80.00,
  enterprise: null,
};

/**
 * Bounded-trial framing for tiers without an ongoing $/mo workflow budget
 * (mirrors `tier_gate::trial_workflow_allowance` in Rust). Observer/Managed
 * get a small, hard-capped taste of the autonomous search feature -- the
 * platform's best activation moment -- without the cost exposure of an
 * ongoing pool. Shown as "N free searches" copy on the billing page rather
 * than forced into the same $/mo progress-bar UI Professional/Team use.
 */
export const TIER_WORKFLOW_TRIAL_LABEL: Partial<Record<TierKey, string>> = {
  observer: '1 free autonomous search (one-time)',
  managed: '1 free autonomous search/mo',
};

/** Display string for a tier's everyday AI credit allotment. */
export function tierAiBudgetLabel(tier: TierKey): string {
  const budget = TIER_AI_BUDGET_USD[tier];
  if (budget == null) return 'Unlimited AI credits';
  return `$${budget.toFixed(2)}/mo AI credits`;
}

/**
 * Display string for a tier's workflow-specific credit allotment: the
 * bounded-trial copy for Observer/Managed, the $/mo figure for
 * Professional/Team, or `null` if the tier has neither (there is none
 * today, but keep the fallback honest).
 */
export function tierWorkflowBudgetLabel(tier: TierKey): string | null {
  const trialLabel = TIER_WORKFLOW_TRIAL_LABEL[tier];
  if (trialLabel) return trialLabel;
  const budget = TIER_WORKFLOW_BUDGET_USD[tier];
  if (budget == null) return null;
  return `$${budget.toFixed(2)}/mo autonomous workflow credits`;
}

// --- Autonomous Credits add-on ---
// Sold as a second Stripe subscription item on a Professional/Team
// tenant's EXISTING subscription (never a second subscription object --
// see program/src/api/billing.rs's `addon_pool_from_price_id` doc comment
// for the downgrade hazard a separate subscription would create). Raises
// `TIER_WORKFLOW_BUDGET_USD`'s ceiling for the rest of the calendar month
// via `program/src/api/ai_usage.rs`'s `addon_credit_this_month` ledger sum
// -- not a new base tier, purely an add-on. Priced at ~$16/effective run
// (roughly 3x the real avg Claude COGS of $5.04/run, p90 $9.95 -- see the
// 30-day workflow_iteration cost analysis this was derived from), because
// a tenant running this genuinely 24/7 (hourly cron) would otherwise cost
// far more in COGS than any flat monthly number could safely absorb.
export interface AutonomousCreditPool {
  /** Mirrors a `StripeConfig.price_autonomous_credits_*` field name (Rust). */
  key: 'pool_150' | 'pool_500' | 'pool_top_tier';
  priceUsd: number;
  /** Matches `StripeConfig::addon_pool_from_price_id`'s pool_label exactly. */
  label: string;
}

export const AUTONOMOUS_CREDIT_POOLS: readonly AutonomousCreditPool[] = [
  { key: 'pool_150', priceUsd: 150, label: '+9 runs/mo' },
  { key: 'pool_500', priceUsd: 500, label: '+31 runs/mo' },
  { key: 'pool_top_tier', priceUsd: 2000, label: '+125 runs/mo' },
];

/**
 * Whether a tier can purchase the Autonomous Credits add-on at all --
 * gated to tiers that already have `Feature::Workflows` (Professional+),
 * since the add-on only ever raises a workflow budget that doesn't exist
 * for Observer/Managed (`TIER_WORKFLOW_BUDGET_USD` is `null` for both).
 */
export function canPurchaseAutonomousCredits(tier: TierKey): boolean {
  return hasFeature(tier, 'workflows');
}

// --- Workflow template access + concurrency/compute gating ---
// Mirrors program/src/api/tier_gate.rs's `allowed_workflow_templates` /
// `min_tier_for_template` / `max_parallel_hypotheses` /
// `max_concurrent_workflow_runs` / `workflow_run_max_ga_evaluations` (quant
// council 2026-07-28 re-gating pass). Keep both in sync.
//
// `strategy_development`/`optimization`/`research_pipeline`/
// `strategy_discovery` removed 2026-09-15: confirmed zero real usage ever
// (0 of 461 workflow_runs) and no live frontend entry point anywhere in
// gui/src outside test fixtures -- each is fully subsumed by the current
// profitable_strategy_search rule-first flow (GA-backed, walk-forward
// built in, literature grounding via cite_literature_source/
// list_known_anomalies now a first-class part of every hypothesis
// verdict). Team no longer has an exclusive template over Professional as
// a result -- neither pair was ever real, so this doesn't remove anything
// a customer could actually use. `strategy_audit` is the one exception:
// it solves a real, still-unaddressed problem (bring-your-own-code
// validation) and is being rebuilt, not removed.

const TRIAL_TEMPLATES = ['profitable_strategy_search'] as const;
const PROFESSIONAL_TEMPLATES = [
  'profitable_strategy_search',
  'strategy_audit',
  'test_specific_hypothesis',
] as const;
const TEAM_AND_UP_TEMPLATES = PROFESSIONAL_TEMPLATES;

/**
 * Which workflow templates a tier can create via the Research page's
 * autonomous-search hand-off.
 */
export function allowedWorkflowTemplates(tier: TierKey): readonly string[] {
  switch (tier) {
    case 'observer':
    case 'managed':
      return TRIAL_TEMPLATES;
    case 'professional':
      return PROFESSIONAL_TEMPLATES;
    case 'team':
    case 'enterprise':
      return TEAM_AND_UP_TEMPLATES;
  }
}

/** First (lowest-rank) tier whose `allowedWorkflowTemplates` includes `template` -- for "Upgrade to X" CTAs. */
export function minTierForTemplate(template: string): TierKey {
  for (const t of TIER_KEYS) {
    if (allowedWorkflowTemplates(t).includes(template)) return t;
  }
  return 'enterprise';
}

/** Per-turn cap on concurrent `submit_full_backtest` dispatch inside a single autonomous workflow run. */
export const TIER_MAX_PARALLEL_HYPOTHESES: Record<TierKey, number> = {
  observer: 1,
  managed: 1,
  professional: 2,
  team: 3,
  enterprise: 4,
};

/** Max simultaneously active (pending/running/paused) workflow runs a tenant may have. `null` = unlimited. */
export const TIER_MAX_CONCURRENT_WORKFLOW_RUNS: Record<TierKey, number | null> = {
  observer: 1,
  managed: 1,
  professional: 2,
  team: 5,
  enterprise: null,
};

/**
 * Per-run ceiling on total GA candidate evaluations (`population_size *
 * generations`, summed across a run's GA-backed submissions) -- a real
 * compute-volume budget, distinct from `TIER_WORKFLOW_BUDGET_USD` (which
 * only tracks Claude token spend). `null` = no per-run compute ceiling.
 */
export const TIER_MAX_GA_EVALUATIONS_PER_RUN: Record<TierKey, number | null> = {
  observer: 0,
  managed: 0,
  professional: 50_000,
  team: 150_000,
  enterprise: null,
};

